import { useEffect } from 'react';
import { SharpeLogo } from '@sharpe/ui/components';
import { useRouter, useSearchParams } from '@/hooks/use-routing';
import Link from '@/components/smart-link';
import { MagicLinkForm } from '@/components/auth/magic-link-form';
import { LoginButton } from '@/components/auth/login-button';
import { useCurrentUser } from '@/hooks/use-current-user-org';
import { trpc } from '@/lib/trpc';
import { motion } from 'framer-motion';
import ColorBends from '@/components/auth/ColorBends';

const FALLBACK_PROVIDER = {
  id: 'magic-link',
  name: 'Magic Link',
  description: 'Enter your details to continue',
  buttonLabel: 'Continue',
  iconPath: undefined,
};

export default function SignInPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const error = searchParams.get('error');
  const isJupyterMissing = error === 'jupyter_user_missing' || error === 'unauthenticated';

  const { isAuthenticated, sessionStatus } = useCurrentUser();
  const providerQuery = trpc.auth.provider.useQuery(undefined, { staleTime: 5 * 60 * 1000 });

  const authProviderMetadata = providerQuery.data ?? FALLBACK_PROVIDER;

  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (isAuthenticated && !isJupyterMissing) {
      router.replace('/');
    }
  }, [isAuthenticated, isJupyterMissing, router, sessionStatus]);

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center p-6">
      {/* ColorBends background */}
      <div className="absolute inset-0">
        <ColorBends
          colors={['#ffffff', '#888888', '#000000']}
          speed={0.14}
          scale={0.1}
          frequency={1.9}
          warpStrength={1.0}
          noise={0.21}
          centerRadius={0.55}
          centerFalloff={0.3}
          ascii
          asciiCellSize={5}
        />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto flex items-center justify-center gap-12">
        {/* Left Side - Sign In Card */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md bg-card rounded-md p-8 shadow-2xl border border-ring/20"
        >
          <SharpeLogo size={36} className="mb-6" />

          <h1 className="text-3xl font-medium text-foreground mb-2">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">
            New user?{' '}
            <Link
              href="/auth/signup"
              className="ml-1 text-foreground hover:text-foreground/70 font-medium no-underline"
            >
              Create an account
            </Link>
          </p>

          {isJupyterMissing && (
            <div className="rounded-md border border-ds-red-400 bg-ds-red-100 px-3 py-2 text-xs text-destructive mb-6">
              <p className="font-medium">Authentication required</p>
              <p className="mt-0.5 text-destructive/80">
                {error === 'jupyter_user_missing'
                  ? 'Jupyter username not found.'
                  : 'Session expired. Please sign in again.'}
              </p>
            </div>
          )}

          {authProviderMetadata.id === 'magic-link' ? (
            <MagicLinkForm mode="signin" />
          ) : (
            <LoginButton
              providerId={authProviderMetadata.id}
              providerName={authProviderMetadata.name}
              iconPath={authProviderMetadata.iconPath}
            />
          )}

          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing, you agree to our{' '}
            <a
              href="https://sharpe.sh/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/70 hover:text-foreground underline underline-offset-2"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href="https://sharpe.sh/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/70 hover:text-foreground underline underline-offset-2"
            >
              Privacy Policy
            </a>
            .
          </p>
        </motion.div>
      </div>
    </div>
  );
}
